import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Ban, CheckCircle2, Search } from "lucide-react";
import { ROLES, groupedRoles, roleLabel } from "@/constants/verticals";
import { useAuth } from "@/contexts/AuthContext";

export default function Users() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [cashBoxes, setCashBoxes] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name:"", email:"", password:"", role:"cashier", company_ids: [], default_cash_box_id: "", custom_role_id: "", max_branches: 0 });
  const [search, setSearch] = useState("");
  const [customRoles, setCustomRoles] = useState([]);
  const [costCenters, setCostCenters] = useState([]);

  const load = async () => {
    try {
      const [u, c, b, cr, cc] = await Promise.all([
        api.get("/users"),
        api.get("/companies"),
        api.get("/cashboxes").catch(()=>({data:[]})),
        api.get("/custom-roles").catch(()=>({data:[]})),
        api.get("/cost-centers").catch(()=>({data:[]})),
      ]);
      setItems(u.data); setCompanies(c.data); setCashBoxes(b.data || []); setCustomRoles(cr.data || []); setCostCenters(cc.data || []);
    } catch(e){ toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name:"", email:"", password:"", role:"cashier", company_ids: [], default_cash_box_id: "", custom_role_id: "", max_branches: 0, branch_cost_center: "" }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ name:u.name, email:u.email, password:"", role:u.role, company_ids: u.company_ids || [], default_cash_box_id: u.default_cash_box_id || "", custom_role_id: u.custom_role_id || "", max_branches: u.max_branches || 0, branch_cost_center: u.branch_cost_center || ""}); setOpen(true); };

  const save = async () => {
    try {
      const isOp = form.role !== "super_admin";
      if (editing) {
        const body = { name: form.name, role: form.role, company_ids: form.company_ids, custom_role_id: form.custom_role_id || "" };
        if (form.password) body.password = form.password;
        // only send default_cash_box_id when role is cashier (empty → unassign)
        body.default_cash_box_id = form.role === "cashier" ? (form.default_cash_box_id || "") : "";
        body.branch_cost_center = isOp ? (form.branch_cost_center || "") : "";
        await api.put(`/users/${editing.id}`, body);
        // Platform admin sets branch limit for company managers
        if (me?.role === "super_admin" && form.role === "company_manager") {
          await api.post(`/users/${editing.id}/branch-limit`, { max_branches: Number(form.max_branches) || 0 }).catch(() => {});
        }
      } else {
        const body = { ...form };
        delete body.max_branches;
        if (!isOp) delete body.branch_cost_center;
        else if (!body.branch_cost_center) delete body.branch_cost_center;
        if (body.role !== "cashier") delete body.default_cash_box_id;
        else if (!body.default_cash_box_id) delete body.default_cash_box_id;
        if (!body.custom_role_id) delete body.custom_role_id;
        await api.post("/users", body);
      }
      toast.success("تم الحفظ"); setOpen(false); load();
    } catch(e){ toast.error(formatErr(e)); }
  };

  const del = async (id) => {
    if (!confirm("حذف المستخدم؟")) return;
    try { await api.delete(`/users/${id}`); toast.success("تم الحذف"); load(); } catch(e){ toast.error(formatErr(e)); }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? "تم تعطيل الحساب" : "تم تفعيل الحساب");
      load();
    } catch(e) { toast.error(formatErr(e)); }
  };

  const toggleCompany = (cid) => {
    setForm((f) => ({...f, company_ids: f.company_ids.includes(cid) ? f.company_ids.filter(x=>x!==cid) : [...f.company_ids, cid]}));
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold">المستخدمون</h1>
          <p className="text-sm text-muted-foreground mt-1">إدارة الحسابات والصلاحيات لكل شركة</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={openNew} data-testid="add-user-button"><Plus className="w-4 h-4 me-1" /> مستخدم جديد</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editing ? "تعديل مستخدم" : "مستخدم جديد"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>الاسم</Label><Input value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} data-testid="user-name-input" /></div>
              <div><Label>الإيميل</Label><Input type="email" value={form.email} disabled={!!editing} onChange={(e)=>setForm({...form, email:e.target.value})} data-testid="user-email-input" /></div>
              <div><Label>كلمة المرور {editing && <span className="text-xs text-muted-foreground">(اتركها فارغة للإبقاء)</span>}</Label><Input type="password" value={form.password} onChange={(e)=>setForm({...form, password:e.target.value})} data-testid="user-password-input" /></div>
              <div>
                <Label>الدور</Label>
                <Select value={form.role} onValueChange={(v)=>setForm({...form, role:v})}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-80">
                    {Object.entries(groupedRoles()).map(([group, items]) => (
                      <React.Fragment key={group}>
                        <div className="px-2 py-1.5 text-[10px] font-bold text-muted-foreground uppercase tracking-wider bg-muted/50 sticky top-0">{group}</div>
                        {items.map(r => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
                      </React.Fragment>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {form.role !== "super_admin" && customRoles.length > 0 && (
                <div>
                  <Label className="flex items-center gap-1.5">
                    <span>الدور المخصص</span>
                    <span className="text-[10px] text-muted-foreground">(اختياري — يُطبَّق مع الدور الأساسي)</span>
                  </Label>
                  <Select value={form.custom_role_id || "__none__"} onValueChange={(v)=>setForm({...form, custom_role_id: v === "__none__" ? "" : v})}>
                    <SelectTrigger data-testid="user-custom-role-select"><SelectValue placeholder="بدون دور مخصص" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— بدون دور مخصص —</SelectItem>
                      {customRoles.map(r => (
                        <SelectItem key={r.id} value={r.id}>
                          {r.name} · {r.permissions?.length || 0} صلاحية
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {form.custom_role_id && (
                    <div className="mt-1 text-[10px] text-muted-foreground">
                      💡 {customRoles.find(r=>r.id===form.custom_role_id)?.description || "دور مخصص نشط"}
                    </div>
                  )}
                </div>
              )}
              {form.role !== "super_admin" && (
                <div>
                  <Label>الشركات المسموحة</Label>
                  <div className="border border-border rounded-md p-2 space-y-1 max-h-40 overflow-auto">
                    {companies.map(c => (
                      <label key={c.id} className="flex items-center gap-2 p-1 cursor-pointer hover:bg-accent rounded">
                        <Checkbox checked={form.company_ids.includes(c.id)} onCheckedChange={()=>toggleCompany(c.id)} />
                        <span className="text-sm">{c.name}</span>
                      </label>
                    ))}
                    {companies.length === 0 && <div className="text-xs text-muted-foreground">لا توجد شركات</div>}
                  </div>
                </div>
              )}
              {me?.role === "super_admin" && editing && form.role === "company_manager" && (
                <div>
                  <Label>الحد الأقصى لعدد الفروع <span className="text-xs text-muted-foreground">(0 = غير محدود)</span></Label>
                  <Input type="number" min="0" value={form.max_branches} onChange={e => setForm({ ...form, max_branches: e.target.value })} data-testid="user-max-branches" />
                  <p className="text-[10px] text-muted-foreground mt-1">أقصى عدد فروع/شركات يستطيع مدير الشركة فتحها بنفسه من صفحة «الفروع».</p>
                </div>
              )}
              {form.role !== "super_admin" && costCenters.length > 0 && (
                <div>
                  <Label>الفرع المخصص للموظف <span className="text-xs text-muted-foreground">(اختياري)</span></Label>
                  <Select value={form.branch_cost_center || "__none__"} onValueChange={(v)=>setForm({...form, branch_cost_center: v === "__none__" ? "" : v})}>
                    <SelectTrigger data-testid="user-branch-select"><SelectValue placeholder="كل الفروع" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— كل الفروع —</SelectItem>
                      {costCenters.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground mt-1">عند الربط بفرع، يُختار هذا الفرع تلقائياً ويُقفل في نقطة البيع والفواتير لهذا الموظف.</p>
                </div>
              )}
              {form.role === "cashier" && (
                <div>
                  <Label>الصندوق المخصص للكاشير <span className="text-xs text-muted-foreground">(اختياري)</span></Label>
                  <Select value={form.default_cash_box_id || "__none__"} onValueChange={(v)=>setForm({...form, default_cash_box_id: v === "__none__" ? "" : v})}>
                    <SelectTrigger data-testid="user-cashbox-select"><SelectValue placeholder="اختر صندوقاً" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— بدون صندوق افتراضي —</SelectItem>
                      {cashBoxes.map(b => (
                        <SelectItem key={b.id} value={b.id}>{b.name || `صندوق ${b.currency}`} · {b.currency}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground mt-1">عند تسجيل دخول الكاشير، سيُختار هذا الصندوق تلقائياً في كل فاتورة/سند.</p>
                </div>
              )}
            </div>
            <DialogFooter><Button onClick={save} data-testid="save-user-button">حفظ</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input placeholder="بحث بالاسم أو الإيميل أو الدور..." value={search} onChange={(e)=>setSearch(e.target.value)} className="ps-9" data-testid="users-search-input" />
      </div>

      <div className="border border-border rounded-md bg-card overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="text-start">الاسم</TableHead>
            <TableHead className="text-start">الإيميل</TableHead>
            <TableHead className="text-start">الدور</TableHead>
            <TableHead className="text-start">الشركات</TableHead>
            <TableHead className="text-start">الحالة</TableHead>
            <TableHead className="text-start">إجراءات</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.filter(u => {
              if (!search.trim()) return true;
              const s = search.toLowerCase();
              const rl = (ROLES.find(r=>r.v===u.role)?.l || "").toLowerCase();
              return (u.name||"").toLowerCase().includes(s) || (u.email||"").toLowerCase().includes(s) || rl.includes(s);
            }).map(u => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-semibold">{u.name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>
                  {u.role === "super_admin"
                    ? (u.is_root
                        ? <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-bold">👑 مدير عام رئيسي</span>
                        : <span className="text-xs px-2 py-0.5 rounded bg-primary/10 text-primary font-bold">مدير عام فرعي</span>)
                    : (
                      <div className="flex flex-col gap-1">
                        <span>{ROLES.find(r=>r.v===u.role)?.l || u.role}</span>
                        {u.custom_role_id && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-bold w-fit" title="دور مخصص" data-testid={`user-crole-${u.id}`}>
                            🔑 {customRoles.find(r=>r.id===u.custom_role_id)?.name || "دور مخصص"}
                          </span>
                        )}
                      </div>
                    )}
                </TableCell>
                <TableCell className="text-xs">{u.role === "super_admin" ? "الكل" : (u.company_ids||[]).length}</TableCell>
                <TableCell>
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded ${u.active !== false ? "bg-emerald-50 text-emerald-700" : "bg-destructive/10 text-destructive"}`} data-testid={`user-status-${u.id}`}>
                    {u.active !== false ? <><CheckCircle2 className="w-3 h-3" /> نشط</> : <><Ban className="w-3 h-3" /> معطل</>}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={()=>toggleActive(u)} data-testid={`toggle-user-${u.id}`} title={u.active !== false ? "تعطيل الدخول" : "تفعيل الدخول"}>
                      {u.active !== false ? <Ban className="w-4 h-4 text-amber-600" /> : <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={()=>openEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={()=>del(u.id)} data-testid={`delete-user-${u.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
